/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Saturation Node
   Luminance-preserving color vibrance and desaturation.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER, GLSL_COLOR_UTILS } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const SATURATION_SHADER = `${GLSL_HEADER}
${GLSL_COLOR_UTILS}
uniform sampler2D u_image;
uniform float u_saturation; // 0.0 .. 2.0 (1.0 = normal)

void main() {
  vec4 src = texture(u_image, v_uv);
  float lum = getLuminance(src.rgb);
  vec3 saturated = mix(vec3(lum), src.rgb, u_saturation);
  fragColor = vec4(clamp(saturated, 0.0, 1.0), src.a);
}
`;

export const saturationNode: NodeDefinition = {
  type: 'color.saturation',
  category: 'Color',
  label: 'Saturation',
  description: 'Adjusts color intensity and vibrance from grayscale to supersaturated',
  keywords: ['saturation', 'vibrance', 'grayscale', 'color', 'monochrome'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Output', type: 'image' },
  ],
  params: [
    {
      id: 'saturation',
      name: 'Saturation',
      type: 'slider',
      defaultValue: 100,
      min: 0,
      max: 250,
      step: 1,
      unit: '%',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: SATURATION_SHADER,
    getUniforms: (inputs, params) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_saturation: (Number(params.saturation) || 100) / 100,
    }),
  }),
};
